import React from "react";
import PropTypes from "prop-types";
import { prependRoot } from "../../util/prependRoot.js";
import { getApiRoot } from "../../store/configureStore.js";
import classnames from "classnames";
import Dropzone from "react-dropzone";
import { showToastrError } from "../../util/toastr.js";
import Tooltip from "../common/Tooltip";
import * as types from "../../constants/ComponentConstants";
import ValidationMessage from "../common/ValidationMessage";
import Row from "../common/Row";
import Loader from "../common/Loader";

const routePrefix = "/document/";

const Files = (props) => {
	const { componentName, downloadFileUrl } = props;
	const hasFiles = !!props.files;
	const hasValidationMessage = !!props.validationMessage;
	const validationMessageType = props.validationMessageType;
	const validation = props.validationMessage ? <ValidationMessage message={props.validationMessage} type={validationMessageType} /> : null;
	const isDownloadOnly = props.isDownloadOnly;
	const canDelete = !!props.onDelete;
	const tooltiptype = props.tooltipType || types.TOOLTIP_INFO;
	const showSpinner = props.isLoading ? props.isLoading : false;
	let info = props.tooltip ? <Tooltip text={props.tooltip} tooltipType={tooltiptype} iconColor={props.tooltipIconColor} windowWidth={props.tooltipWindowWidth} /> : null;

	const center = (<div className="o-col-1">&nbsp;</div>);
	const sizeClassName = props.center ? "o-col-10" : (props.size ? "o-col-" + props.size : "o-col-12");

	function handleDelete(event) {
		let confirmed = confirm("Weet u zeker dat u dit document wil verwijderen?");
		if (confirmed === true) {
			props.onDelete(event.target.getAttribute('value'));
		}
	}

	function onRejected(event) {
		showToastrError("Het is enkel mogelijk om bestanden van het type " + props.fileTypes.extensions + " op te laden.");
	}

	// je kan ook een file extension meegeven als accept property op de dropzone (cfr GPX) --> https://react-dropzone.netlify.com/
	let allowedMimeTypes = (props.fileTypes && props.fileTypes.mimeTypes) ? props.fileTypes.mimeTypes : types.DEFAULT_MIME_TYPES;
	let allowedExtensions = (props.fileTypes && props.fileTypes.extensions) ? props.fileTypes.extensions : types.DEFAULT_FILE_EXTENSIONS;

	let body;
	if (showSpinner)
		body = <Loader />;
	else if (isDownloadOnly)
		body = (!hasFiles || props.files.length == 0) ? "Nog geen documenten beschikbaar" : null;
	else if (props.disabled) {
		let dropzoneProps = {
			className: "c-dropzone",
			disabled: true
		};

		body = <Dropzone {...dropzoneProps}>
			<div className={"uploadzone"}>
				{!(!hasFiles || props.files.length == 0) || "Geen documenten beschikbaar"}
			</div>
		</Dropzone>;
	}
	else {
		let dropzoneProps = {
			className: "c-dropzone",
			accept: allowedMimeTypes,
			onDrop: props.onDrop,
			onDropRejected: onRejected,
			activeClassName: "c-dropzone--accepted",
			rejectClassName: "c-dropzone--rejected"
		};

		let filetypeText = "Toegelaten formaten " + allowedExtensions.replace(/,(?=[^,]*$)/, ' en') + ".";

		if (props.tooltip)
			info = <Tooltip text={props.tooltip + "\n" + filetypeText} tooltipType={tooltiptype} iconColor={props.tooltipIconColor} windowWidth={props.tooltipWindowWidth} />;

		body = (
			<Dropzone {...dropzoneProps}>
				<div className={"uploadzone"}>
					Sleep hier uw document(en) of klik hier om te bladeren
				</div>
			</Dropzone>
		);
	}

	const fileList = hasFiles ? (
		<ul className="filelist">
			{
				props.files.map((file, i) => {
					let urlParam = file;
					let path = null;

					if (typeof file != "string") {
						path = downloadFileUrl + componentName.replace("/", "$") + "/" + file.urlParam;
						file = file.display;
					}
					else
						path = downloadFileUrl + componentName.replace("/", "$") + "/" + urlParam;

					const delIcon = canDelete && !props.disabled ? <i className="fa fa-times deletebutton" value={file} onClick={handleDelete}></i> : null;
					return (
						<li key={file}>
							{delIcon}
							{path && !props.downloadFunction ?
								<a href={path}>
									{file}
								</a> :
								<a href="#" onClick={props.downloadFunction}>
									{file}
								</a>}
						</li>);
				})
			}
		</ul >) : null;

	let cFicheClassNames = {
		"c-fiche": true,
		[props.color]: true,
		"c-fiche--downloaduploadfiles": true,
		"disabled": props.disabled,
		"error": hasValidationMessage,
		"warning": hasValidationMessage && validationMessageType === types.ValidationMessageTypes.WARNING
	};

	let divClassname = "";
	if (!showSpinner)
		divClassname = "c-fiche__body";

	const component = (<div className={classnames(sizeClassName)}>
		<div className={classnames("c-fiche-wrap", "u-clear")}>
			<div className={classnames(cFicheClassNames)}>
				<header className="c-fiche__header">
					<h1 className="title"><span dangerouslySetInnerHTML={{ __html: props.title + (props.optional ? " (Optioneel)" : "") }}></span>{info}</h1>
				</header>
				<div className={divClassname}>
					{body}
					{fileList}
				</div>
			</div>
		</div>
		{validation}
	</div>);

	return (props.center ? <Row>{center}{component}</Row> : component);
};

Files.propTypes = {
	title: PropTypes.string.isRequired,
	color: PropTypes.string.isRequired,
	componentName: PropTypes.string.isRequired,
	downloadFileUrl: PropTypes.string,
	disabled: PropTypes.bool,
	dossierId: PropTypes.string,
	validationMessage: PropTypes.string,
	validationMessageType: PropTypes.string,
	size: PropTypes.number,
	center: PropTypes.bool,
	onDrop: PropTypes.func,
	onDelete: PropTypes.func,
	files: PropTypes.array,
	downloadFunction: PropTypes.func,
	fileTypes: PropTypes.object,
	optional: PropTypes.bool,
	tooltip: PropTypes.string,
	tooltipType: PropTypes.string,
	tooltipIconColor: PropTypes.string,
	tooltipWindowWidth: PropTypes.number,
	isDownloadOnly: PropTypes.bool,
	isLoading: PropTypes.bool
};

export default Files;


						/***************
						 * MIMETYPES MAPPING
						.docx   application/vnd.openxmlformats-officedocument.wordprocessingml.document
						.doc    application/msword
						.xls    application/vnd.ms-excel
						.xls    application/msexcel
						.xls    application/x-excel
						.xls    application/x-msexcel
						.xlsx   application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
						.png    image/png
						.jpeg   image/jpeg
						.jpg    image/jpeg
						.gif	image/gif
						.tiff	image/tiff
						.pdf    application/pdf
						.csv	.csv        => gekende bug : mimetype text/csv werkt niet !!
						.ppt	application/vnd.ms-powerpoint
						.pptx   application/vnd.openxmlformats-officedocument.presentationml.presentation
***************/